import {
  type LoaderFunctionArgs,
  type MetaFunction,
  json,
} from '@remix-run/node'
import {
  type ShouldRevalidateFunction,
  useLoaderData,
  useSearchParams,
} from '@remix-run/react'
import { memo, useCallback } from 'react'
import { type CarFilterKey } from '#app/entities/car/filters.js'
import {
  CarService,
  type FilterAggregates,
} from '#app/entities/car/service.server.js'
import { getEncargaUnCocheUrl } from '#app/shared/lib/external-links.ts'
import { getMetaTags } from '#app/shared/lib/meta-tags.ts'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import Header from '#app/shared/ui/layout/Header.js'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import {
  Breadcrumb,
  breadcrumbHome,
} from '#app/shared/ui/navigation/Breadcrumb.js'
import { FiltersNav } from './ui/FiltersNav'
import { FilterTags } from './ui/FilterTags'
import { InfiniteScroll } from './ui/infiniteScroll'
import LinkCard from './ui/LinkCard'
import SortButton from './ui/SortButton'

type Filters = Record<CarFilterKey, string[]>

const decodeParam = (param: string | null) => {
  return param ? decodeURIComponent(param) : null
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const sortField = url.searchParams.get('sort') || ''

  const filters: Filters = {
    brands:
      decodeParam(url.searchParams.get('brands'))?.split(',').filter(Boolean) ||
      [],
    carroceria:
      decodeParam(url.searchParams.get('carroceria'))
        ?.split(',')
        .filter(Boolean) || [],
    color:
      decodeParam(url.searchParams.get('color'))?.split(',').filter(Boolean) ||
      [],
    puertas:
      decodeParam(url.searchParams.get('puertas'))
        ?.split(',')
        .filter(Boolean) || [],
    plazas:
      decodeParam(url.searchParams.get('plazas'))?.split(',').filter(Boolean) ||
      [],
    combustible: (() => {
      const allValues = url.searchParams.getAll('combustible')
      if (allValues.length > 1) {
        return allValues.map(decodeParam).filter(Boolean)
      }
      return (
        decodeParam(url.searchParams.get('combustible'))
          ?.split(',')
          .filter(Boolean) || []
      )
    })(),
    etiqueta:
      decodeParam(url.searchParams.get('etiqueta'))
        ?.split(',')
        .filter(Boolean) || [],
    kmMin: url.searchParams.get('kmMin')
      ? [url.searchParams.get('kmMin')!]
      : [],
    kmMax: url.searchParams.get('kmMax')
      ? [url.searchParams.get('kmMax')!]
      : [],
    yearMin: url.searchParams.get('yearMin')
      ? [url.searchParams.get('yearMin')!]
      : [],
    yearMax: url.searchParams.get('yearMax')
      ? [url.searchParams.get('yearMax')!]
      : [],
    traccion:
      decodeParam(url.searchParams.get('traccion'))
        ?.split(',')
        .filter(Boolean) || [],
    cambio:
      decodeParam(url.searchParams.get('cambio'))?.split(',').filter(Boolean) ||
      [],
    powerMin: url.searchParams.get('powerMin')
      ? [url.searchParams.get('powerMin')!]
      : [],
    powerMax: url.searchParams.get('powerMax')
      ? [url.searchParams.get('powerMax')!]
      : [],
    models:
      decodeParam(url.searchParams.get('models'))?.split(',').filter(Boolean) ||
      [],
    priceMin: url.searchParams.get('priceMin')
      ? [url.searchParams.get('priceMin')!]
      : [],
    priceMax: url.searchParams.get('priceMax')
      ? [url.searchParams.get('priceMax')!]
      : [],
    deducibleIva: url.searchParams.get('deducibleIva') ? ['true'] : [],
    financingMin: url.searchParams.get('financingMin')
      ? [url.searchParams.get('financingMin')!]
      : [],
    financingMax: url.searchParams.get('financingMax')
      ? [url.searchParams.get('financingMax')!]
      : [],
    manufacturerBatteryWarranty: url.searchParams.get(
      'manufacturerBatteryWarranty',
    )
      ? ['true']
      : [],
  }

  const INITIAL_BATCH_SIZE = 11

  const [cars, totalCount, aggregates, maxMileage, minYear] = await Promise.all(
    [
      CarService.filterCars(filters, sortField, { limit: INITIAL_BATCH_SIZE }),
      CarService.countFilteredCars(filters),
      CarService.getFilterAggregates(filters),
      CarService.getMaxMileage(),
      CarService.getMinYear(),
    ],
  )

  return json({
    cars,
    totalCount,
    aggregates,
    filters,
    sortField,
    maxMileage,
    minYear,
  })
}

// Prevent loader revalidation when the infinite-scroll fetcher hits the paginate
// API. Otherwise each fetch would reset initialCars → wipe the appended batches.
export const shouldRevalidate: ShouldRevalidateFunction = ({
  formAction,
  defaultShouldRevalidate,
}) => {
  if (formAction === '/api/cars/paginate') return false
  return defaultShouldRevalidate
}

export const meta: MetaFunction = ({ matches }) => {
  return getMetaTags({
    matches,
    title: 'Nuestros Coches | Gama Óptima',
    description:
      'Explora nuestro catálogo de coches de ocasión con entrega inmediata. Mercedes, BMW, Audi, Porsche y más. Ocasión premium con garantía y financiación en Barcelona.',
    url: 'https://gamaoptima.com/nuestros-coches',
  })
}

const FiltersSection = memo(function FiltersSection({
  aggregates,
  totalCount,
  currentSort,
  onSortChange,
  filters,
  onRemoveFilter,
  onRemoveSort,
  onClearAll,
  maxMileage,
  minYear,
}: {
  aggregates: FilterAggregates
  totalCount: number
  currentSort: string
  onSortChange: (sort: string) => void
  filters: Filters
  onRemoveFilter: (filter: string) => void
  onRemoveSort: () => void
  onClearAll: () => void
  maxMileage?: number
  minYear?: number
}) {
  return (
    <>
      <FiltersNav
        aggregates={aggregates}
        isMobile={false}
        maxMileage={maxMileage}
        minYear={minYear}
      />
      <div className="bg-neutral-25">
        <MainContainer>
          <FilterTags
            filters={filters}
            currentSort={currentSort}
            onRemoveFilter={onRemoveFilter}
            onRemoveSort={onRemoveSort}
            onClearAll={onClearAll}
          />

          <section
            className={`flex w-full items-center justify-between bg-neutral-25`}
          >
            <div
              className={`
                hidden w-full items-center justify-between pt-4

                lg:flex
              `}
            >
              <div
                className={`
                  text-subtitle1-medium tracking-[-0.04em] text-neutral-400
                `}
              >
                {totalCount} coche{totalCount === 1 ? '' : 's'} disponible
                {totalCount !== 1 ? 's' : ''}
              </div>
              <SortButton
                currentSort={currentSort}
                onSortChange={onSortChange}
                isMobile={false}
              />
            </div>
          </section>
        </MainContainer>
      </div>
    </>
  )
})

const MobileControls = memo(function MobileControls({
  aggregates,
  currentSort,
  onSortChange,
  maxMileage,
  minYear,
}: {
  aggregates: FilterAggregates
  currentSort: string
  onSortChange: (sort: string) => void
  maxMileage?: number
  minYear?: number
}) {
  return (
    <div
      className={`
        flex w-full

        lg:hidden
      `}
    >
      <div className="flex-1">
        <FiltersNav
          aggregates={aggregates}
          isMobile={true}
          maxMileage={maxMileage}
          minYear={minYear}
        />
      </div>
      <div className="flex-1">
        <SortButton
          currentSort={currentSort}
          onSortChange={onSortChange}
          isMobile={true}
        />
      </div>
    </div>
  )
})

export default function CochesUsados() {
  const {
    cars,
    totalCount,
    aggregates,
    filters,
    sortField,
    maxMileage,
    minYear,
  } = useLoaderData<typeof loader>()
  const [searchParams, setSearchParams] = useSearchParams()

  const handleSortChange = useCallback(
    (sortValue: string) => {
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set('sort', sortValue)
      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams],
  )

  const handleRemoveSort = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete('sort')
    setSearchParams(newSearchParams)
  }, [searchParams, setSearchParams])

  const handleClearAll = useCallback(() => {
    setSearchParams(new URLSearchParams())
  }, [setSearchParams])

  const handleRemoveFilter = useCallback(
    (filterKey: string, filterValue: string) => {
      const newSearchParams = new URLSearchParams(searchParams.toString())

      // Special case for removing all hybrid types
      if (filterKey === 'combustible' && filterValue === 'ALL_HYBRIDS') {
        const hybridTypes = ['Híbrido', 'Híbrido enchufable', 'Mild Hybrid']

        // Remove all combustible parameters first
        newSearchParams.delete(filterKey)

        // Get remaining combustible values that are not hybrid types
        const allCombustibleValues = searchParams.getAll(filterKey)
        const nonHybridValues = allCombustibleValues.filter(
          (value) => !hybridTypes.includes(decodeURIComponent(value)),
        )

        // Add back non-hybrid combustible values if any
        if (nonHybridValues.length > 0) {
          newSearchParams.set(filterKey, nonHybridValues.join(','))
        }
      } else {
        // Normal single filter removal
        const currentFilters = searchParams.get(filterKey)?.split(',') || []
        const updatedFilters = currentFilters.filter((f) => f !== filterValue)

        if (updatedFilters.length > 0) {
          newSearchParams.set(filterKey, updatedFilters.join(','))
        } else {
          newSearchParams.delete(filterKey)
        }
      }

      setSearchParams(newSearchParams)
    },
    [searchParams, setSearchParams],
  )

  return (
    <div>
      <Header isSticky={true} showBanner={true} />
      <div className="bg-white pb-8">
        <MainContainer>
          <section
            className={`
              mx-auto w-full

              lg:px-2

              sm:px-10
            `}
          >
            <Breadcrumb items={[breadcrumbHome, { label: 'Ocasión' }]} />
            <h4
              className={`
                px-4 text-h4

                lg:pl-0

                sm:pl-4
              `}
            >
              Coches disponibles con entrega inmediata
            </h4>
          </section>
        </MainContainer>
      </div>

      <MobileControls
        aggregates={aggregates}
        currentSort={sortField}
        onSortChange={handleSortChange}
        maxMileage={maxMileage}
        minYear={minYear}
      />

      <section
        className={`
          mx-auto w-full

          lg:px-2

          sm:px-10
        `}
      >
        <div
          className={`
            mt-4 px-4 text-subtitle1-medium tracking-[-0.04em] text-neutral-400

            lg:hidden lg:pl-0

            sm:pl-4
          `}
        >
          {totalCount} coche{totalCount === 1 ? '' : 's'} disponible
          {totalCount !== 1 ? 's' : ''}
        </div>
      </section>

      <FiltersSection
        aggregates={aggregates}
        totalCount={totalCount}
        currentSort={sortField}
        onSortChange={handleSortChange}
        filters={filters}
        onRemoveFilter={(filter) => {
          const [key, value] = filter.split(':')
          if (key && value) {
            handleRemoveFilter(key, value)
          }
        }}
        onRemoveSort={handleRemoveSort}
        onClearAll={handleClearAll}
        maxMileage={maxMileage}
        minYear={minYear}
      />

      <div className="bg-neutral-25">
        <MainContainer>
          <Spacer size="16px" />

          <section className="bg-neutral-25">
            <MainContainer>
              <div>
                <InfiniteScroll
                  initialCars={cars}
                  totalCount={totalCount}
                  filters={filters}
                  sortField={sortField}
                  className={`
                    px-4

                    lg:px-0

                    md:px-14

                    sm:px-6
                  `}
                />
              </div>
              <div className="mt-5 flex w-full justify-center pb-8">
                <LinkCard
                  image="/img/LinkCards/LinkCard.png"
                  title="¿No encuentras lo que buscas?"
                  description="Lo buscamos para ti."
                  buttonText="Encargar coche"
                  footerText="Del mundo a la puerta de tu casa."
                  backgroundColor="transparent"
                  footerTextColor="var(--neutral-400)"
                  withFrame={false}
                  textColor="black"
                  isLast={true}
                  buttonLink={getEncargaUnCocheUrl()}
                />
              </div>
            </MainContainer>
          </section>
        </MainContainer>
      </div>
    </div>
  )
}
